import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Layers, AlertTriangle, Trash2, X } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useCommandesImpression, useCreateCommandeImpression,
  useUpdateCommandeImpression, useDeleteCommandeImpression,
} from '@/hooks/useTextprint';
import { useContacts } from '@/hooks/useCrm';
import {
  STATUT_COMMANDE_IMPRESSION_LABELS,
  type CommandeImpression, type LigneCommande, type StatutCommandeImpression,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutCommandeImpression, string> = {
  confirmee:        'ds-bd ds-bd-n',
  en_production:   'ds-bd ds-bd-i',
  controle_qualite: 'ds-bd ds-bd-w',
  prete:            'ds-bd ds-bd-s',
  livree:           'ds-bd ds-bd-s',
  facturee:         'ds-bd ds-bd-s',
  soldee:           'ds-bd ds-bd-s',
  annulee:          'ds-bd ds-bd-d',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

export const CommandesImpressionPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]     = useState<StatutCommandeImpression | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]   = useState<CommandeImpression | undefined>();
  const [error, setError]       = useState<string | null>(null);

  const { data, isLoading, refetch } = useCommandesImpression({ statut: statut || undefined, limit: 200 });
  const commandes = data?.data ?? [];
  const remove = useDeleteCommandeImpression();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (c: CommandeImpression) => { setEditing(c); setDialogOpen(true); };

  const onRemove = async (id: string, ref: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer la commande "${ref}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const total = commandes.reduce((s, c) => s + Number(BigInt(c.totalTtcCents)) / 100, 0);
  const enCours = commandes.filter((c) => c.statut !== 'soldee' && c.statut !== 'annulee').length;

  return (
    <>
      <Topbar
        title="Commandes Textprint"
        subtitle={filiale ? `${commandes.length} commande(s) · ${filiale.fullName}` : 'Commandes'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouvelle commande</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">📦</div><div className="ds-kr-val">{commandes.length}</div><div className="ds-kr-lbl">Total commandes</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">🚀</div><div className="ds-kr-val">{enCours}</div><div className="ds-kr-lbl">En cours</div></div>
          <div className="ds-kr kr-c5"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(total)}</div><div className="ds-kr-lbl">Total TTC (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutCommandeImpression | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_COMMANDE_IMPRESSION_LABELS) as StatutCommandeImpression[]).map((s) => <option key={s} value={s}>{STATUT_COMMANDE_IMPRESSION_LABELS[s]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Commandes</div><div className="ds-table-sub">{commandes.length} commande{commandes.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 150 }}>Réf</th>
              <th>Client</th>
              <th className="ds-am" style={{ width: 70 }}>Lignes</th>
              <th style={{ width: 90 }}>Date</th>
              <th style={{ width: 90 }}>Livraison</th>
              <th className="ds-am" style={{ width: 130 }}>Total TTC</th>
              <th className="ds-am" style={{ width: 110 }}>Acompte</th>
              <th style={{ width: 120 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && commandes.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                  <Layers size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune commande</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer la première commande</button>
                </td></tr>
              )}
              {!isLoading && commandes.map((c) => (
                <tr key={c.id}>
                  <td><span className="ds-ref-code">{c.reference}</span></td>
                  <td style={{ fontSize: 12 }}>{c.client?.companyName ?? c.client?.fullName ?? '—'}</td>
                  <td className="ds-am">{c._count?.ordres ?? c.lignes?.length ?? 0}</td>
                  <td style={{ fontSize: 11 }}>{fmtDate(c.dateCommande)}</td>
                  <td style={{ fontSize: 11 }}>{fmtDate(c.dateLivraisonPrevue)}</td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{formatMontant(c.totalTtcCents)}</td>
                  <td className="ds-am" style={{ fontSize: 11, color: 'var(--success)' }}>{formatMontant(c.acompteCents)}</td>
                  <td><span className={STATUT_BADGE[c.statut]}>{STATUT_COMMANDE_IMPRESSION_LABELS[c.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(c)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(c.id, c.reference)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CommandeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} commande={editing} />
    </>
  );
};

const TVA_PCT = 0.18;
const newLigne = (): LigneCommande => ({
  designation: '', quantite: 1, unite: 'pcs', prixUnitaireCents: 0, remisePct: 0, montantCents: 0,
});

const CommandeFormDialog = ({ open, onClose, commande }: { open: boolean; onClose: () => void; commande?: CommandeImpression }) => {
  const isEdit = !!commande;
  const [reference, setReference] = useState('');
  const [clientId, setClientId]   = useState('');
  const [dateLivraison, setDateLivraison] = useState('');
  const [lignes, setLignes]       = useState<LigneCommande[]>([newLigne()]);
  const [acompte, setAcompte]     = useState('');
  const [statut, setStatut]       = useState<StatutCommandeImpression>('confirmee');
  const [adresse, setAdresse]     = useState('');
  const [instructions, setInstructions] = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  const { data: contactsData } = useContacts({ limit: 200 });
  const contacts = (contactsData?.data ?? []).filter((c) => c.contactType === 'client');
  const create = useCreateCommandeImpression();
  const update = useUpdateCommandeImpression();

  useEffect(() => {
    if (!open) return;
    if (commande) {
      setReference(commande.reference); setClientId(commande.clientId ?? '');
      setDateLivraison(commande.dateLivraisonPrevue?.slice(0, 10) ?? '');
      setLignes(commande.lignes?.length ? commande.lignes : [newLigne()]);
      setAcompte(String(Number(BigInt(commande.acompteCents)) / 100));
      setStatut(commande.statut);
      setAdresse(commande.adresseLivraison ?? '');
      setInstructions(commande.instructions ?? '');
      setNotes(commande.notes ?? '');
    } else {
      setReference(''); setClientId(''); setDateLivraison('');
      setLignes([newLigne()]); setAcompte('0'); setStatut('confirmee');
      setAdresse(''); setInstructions(''); setNotes('');
    }
    setError(null);
  }, [open, commande]);

  const updateLigne = (i: number, field: keyof LigneCommande, value: string | number) => {
    setLignes((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const next = { ...l, [field]: value } as LigneCommande;
      const qte = Number(next.quantite) || 0;
      const pu  = Number(next.prixUnitaireCents) || 0;
      const rem = Number(next.remisePct ?? 0);
      next.montantCents = Math.round(qte * pu * (1 - rem / 100));
      return next;
    }));
  };
  const addLigne    = () => setLignes((prev) => [...prev, newLigne()]);
  const removeLigne = (i: number) => setLignes((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const totalHt  = lignes.reduce((s, l) => s + Number(l.montantCents), 0);
  const tva      = Math.round(totalHt * TVA_PCT);
  const totalTtc = totalHt + tva;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const lignesValides = lignes.filter((l) => l.designation.trim() && l.quantite > 0 && l.prixUnitaireCents >= 0);
    if (lignesValides.length === 0) {
      setError('Au moins une ligne valide est requise');
      return;
    }
    try {
      const payload = {
        reference: reference.trim() || undefined,
        clientId: clientId || undefined,
        dateLivraisonPrevue: dateLivraison || undefined,
        lignes: lignesValides,
        totalHtCents: totalHt,
        tvaCents: tva,
        totalTtcCents: totalTtc,
        acompteCents: acompte ? Math.round(parseFloat(acompte) * 100) : 0,
        statut,
        adresseLivraison: adresse.trim() || undefined,
        instructions: instructions.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      if (isEdit && commande) await update.mutateAsync({ id: commande.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${commande?.reference}` : 'Nouvelle commande Textprint'} subtitle="Réf TX-CMD-AAAA-NNNN auto si vide" size="xl">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Référence"><input value={reference} onChange={(e) => setReference(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="auto si vide" /></Field>
          <Field label="Client">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Sans client (devis libre) —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutCommandeImpression)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_COMMANDE_IMPRESSION_LABELS) as StatutCommandeImpression[]).map((s) => <option key={s} value={s}>{STATUT_COMMANDE_IMPRESSION_LABELS[s]}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date livraison prévue"><input type="date" value={dateLivraison} onChange={(e) => setDateLivraison(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Adresse livraison"><input value={adresse} onChange={(e) => setAdresse(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label className="text-xs font-semibold text-gray-700">Lignes de produits</label>
            <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={addLigne}><Plus size={12} /> Ligne</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 70px 110px 70px 110px 30px', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--tx-3)', marginBottom: 4 }}>
            <div>Désignation</div><div>Qté</div><div>Unité</div><div>PU HT</div><div>Rem %</div><div className="ds-am">Total</div><div></div>
          </div>
          {lignes.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 70px 70px 110px 70px 110px 30px', gap: 6, marginBottom: 4 }}>
              <input value={l.designation} onChange={(e) => updateLigne(i, 'designation', e.target.value)} className="ds-fi" style={{ fontSize: 11 }} placeholder="500 flyers A5..." />
              <input type="number" min="1" value={l.quantite} onChange={(e) => updateLigne(i, 'quantite', parseInt(e.target.value || '1', 10))} className="ds-fi" style={{ fontSize: 11 }} />
              <input value={l.unite} onChange={(e) => updateLigne(i, 'unite', e.target.value)} className="ds-fi" style={{ fontSize: 11 }} />
              <input type="number" min="0" value={l.prixUnitaireCents / 100} onChange={(e) => updateLigne(i, 'prixUnitaireCents', Math.round(parseFloat(e.target.value || '0') * 100))} className="ds-fi" style={{ fontSize: 11 }} />
              <input type="number" min="0" max="100" value={l.remisePct ?? 0} onChange={(e) => updateLigne(i, 'remisePct', parseFloat(e.target.value || '0'))} className="ds-fi" style={{ fontSize: 11 }} />
              <div className="ds-am" style={{ fontWeight: 600, lineHeight: '28px' }}>{formatNumber(l.montantCents / 100)}</div>
              <button type="button" onClick={() => removeLigne(i)} className="ds-row-btn" disabled={lignes.length === 1} title="Retirer"><X size={12} /></button>
            </div>
          ))}
          <div style={{ marginTop: 8, padding: 8, background: 'var(--p-soft)', borderRadius: 4, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 12 }}>
            <div>Sous-total HT : <strong>{formatNumber(totalHt / 100)}</strong></div>
            <div>TVA (18%) : <strong>{formatNumber(tva / 100)}</strong></div>
            <div style={{ color: 'var(--p)' }}>Total TTC : <strong>{formatNumber(totalTtc / 100)}</strong></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Acompte reçu (FCFA)"><input type="number" min="0" step="1" value={acompte} onChange={(e) => setAcompte(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="0" /></Field>
          <Field label="Instructions"><input value={instructions} onChange={(e) => setInstructions(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="finition mate, livraison sur site..." /></Field>
        </div>

        <Field label="Notes internes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer la commande'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
